import { CARDART, type Dict, type LabelType } from '@/defs/misc'

export const WP_ENABLED: boolean = true

export type WPRestAPIPostResponseType = {
  _embedded?: {
    author?: {
      avatar_urls?: Dict<string>
      description?: string
      name?: string
      slug: string
    }[]
    'wp:featuredmedia'?: {
      media_details?: {
        sizes?: Dict<{
          height?: number
          source_url?: string
          width?: number
        }>
      }
      source_url?: string
    }[]
    'wp:term'?: {
      id?: number
      name?: string
      slug?: string
      taxonomy?: string
    }[][]
  }
  acf?: {
    featured_image_textless?: string
    guide?:
      | false
      | {
          chapter?: string
          icon?: string
          sections?: {
            section?: string
            section_content?: string
          }[]
        }[]
    short_title?: string
  }
  content?: {
    rendered?: string
  }
  date?: string
  excerpt?: {
    rendered?: string
  }
  id?: number
  slug?: string
  title?: {
    rendered?: string
  }
  yoast_head_json?: {
    description?: string
    og_image?: { url?: string }[]
    title?: string
  }
}

export type WPAuthorType = {
  avatarUrl?: string
  description?: string
  name?: string
  slug?: string
}

export type WPGuideType = {
  chapter: string
  icon: string
  sections: {
    content: string
    label: string
    slug: string
  }[]
}[]

export type WPPostPreviewType = {
  author?: WPAuthorType
  category?: WPCategoryType
  date?: string
  excerpt?: string
  externalLink?: string
  featuredImageUrl?: {
    full?: string
    large?: string
    medium?: string
    textless?: string
  }
  slug: string
  title: string
  youtubeId?: string
}

export type WPPostType = WPPostPreviewType & {
  content: string
  databaseId?: string
  guide?: WPGuideType
  isPreview?: boolean
  postType: 'page' | 'post' | 'guide'
  shortTitle?: string
  tags?: WPTagType[]
}

export type WPTagType = {
  id: number
  name: string
  slug: string
}

export type WPCategoryType = {
  id: number
  label: string
  slug: string
}

export type WPUserType = {
  id: number
}

export type WPOptionsResponseType = {
  alert_description?: string
  alert_heading?: string
  alert_link_url?: string
  alert_scryfallid?: string
}

export const WORDPRESS_CATEGORY_SLUGS_MAIN = [
  'cedh',
  'changelog',
  'data',
  'deck-techs',
  'guide',
  'lists',
  'news-and-reviews',
  'opinion',
] as const
export type WordpressCategorySlugMainType = (typeof WORDPRESS_CATEGORY_SLUGS_MAIN)[number]
export const isWordpressCategorySlugMainType = (slug?: string): slug is WordpressCategorySlugMainType =>
  WORDPRESS_CATEGORY_SLUGS_MAIN.includes(slug as WordpressCategorySlugMainType)
export const WORDPRESS_CATEGORY_SLUGS_ADDITIONAL = ['articles', 'podcast', 'content'] as const
export const WORDPRESS_CATEGORY_SLUGS = [
  ...WORDPRESS_CATEGORY_SLUGS_MAIN,
  ...WORDPRESS_CATEGORY_SLUGS_ADDITIONAL,
] as const
export type WordpressCategorySlugType = (typeof WORDPRESS_CATEGORY_SLUGS)[number]
export const isWordpressCategorySlugType = (slug?: string): slug is WordpressCategorySlugType =>
  WORDPRESS_CATEGORY_SLUGS.includes(slug as WordpressCategorySlugType)
type WordpressCategoryLabelType = LabelType<WordpressCategorySlugType, { id: number; imageArt: string }>
export const WORDPRESS_CATEGORY_LABELS: WordpressCategoryLabelType = {
  articles: { id: 4, imageArt: CARDART.DENRY_KLIN, label: 'Articles' },
  cedh: { id: 29916, imageArt: CARDART.THASSAS_ORACLE, label: 'cEDH' },
  changelog: { id: 30054, imageArt: CARDART.WINDS_OF_CHANGE, label: 'Changelog' },
  content: { id: 380, imageArt: CARDART.ATRAXA_PRAETORS_VOICE_2XM, label: 'Content' },
  data: { id: 29917, imageArt: CARDART.CYBERNETICA_DATASMITH, label: 'Data' },
  'deck-techs': { id: 29918, imageArt: CARDART.BREAKTHROUGH, label: 'Deck Techs' },
  guide: { id: 26504, imageArt: CARDART.SYGG_RIVER_GUIDE, label: 'Guides' },
  lists: { id: 29919, imageArt: CARDART.ALL_FATES_SCROLL, label: 'Lists' },
  'news-and-reviews': { id: 29920, imageArt: CARDART.DENRY_KLIN, label: 'News & Reviews' },
  opinion: { id: 29921, imageArt: CARDART.CRIME_NOVELIST, label: 'Opinion' },
  podcast: { id: 19988, imageArt: CARDART.ATRAXA_PRAETORS_VOICE_2XM, label: 'Podcast' },
}

export const WP_REVALIDATE = 900
export const WP_CACHE_CONTROL = `public, s-maxage=3600, stale-while-revalidate=${WP_REVALIDATE}`
